// The one list Review scrolls.
//
// The alignment is deliberately a single list of rows rather than two
// synchronised scrollers: two scrollers drift, and a drift of one line is
// exactly the misreading this screen exists to prevent. So a row is a
// two-column grid, and both columns of a pair are always the same row.

import type { AlignmentPair, DroppedNode, ScriptView } from "@contract";

/** A Block beside the Manuscript nodes it was made from. */
export interface PairRow {
  readonly kind: "pair";
  readonly key: string;
  readonly pair: AlignmentPair;
}

/** A Manuscript node no Block covers: a warn row, and the reason Approve asks first. */
export interface DroppedRow {
  readonly kind: "dropped";
  readonly key: string;
  readonly node: DroppedNode;
}

export type ReviewRow = PairRow | DroppedRow;

/**
 * Interleaves the pairs with the dropped nodes at the place the Author would
 * have read them: a dropped node sits after the Block that covers the text
 * before it, and `afterBlockIndex: null` means it comes before the first
 * Block.
 *
 * The anchor is resolved to the last pair at or before it rather than matched
 * exactly, because the two numbers live in different spaces: the anchor counts
 * Blocks of the version, while the rows are keyed on `block.index`, and a
 * script whose pairs skip an index would file the node under a key no pair
 * carries. Losing a row that way would lose the paragraph, which is the one
 * bug this row exists to make impossible — `stats.droppedNodes` still counts
 * it, so Approve would ask the Author to accept the loss of something the
 * screen never showed them.
 */
export function reviewRows(script: ScriptView | null): ReviewRow[] {
  if (!script) return [];
  const after = new Map<number, DroppedNode[]>();
  const leading: DroppedNode[] = [];
  for (const node of script.droppedNodes) {
    const anchor = node.afterBlockIndex === null ? null : anchorPair(script, node.afterBlockIndex);
    if (anchor === null) {
      leading.push(node);
      continue;
    }
    const bucket = after.get(anchor);
    if (bucket) bucket.push(node);
    else after.set(anchor, [node]);
  }
  const rows: ReviewRow[] = leading.map(droppedRow);
  for (const pair of script.pairs) {
    // Keyed by the Block's place in the script, not by its id: committing an
    // edit against a refinement version forks a new version, and every Block in
    // a fork has a new id. Keying on the id would unmount every row, and the
    // Author would lose the focus on the Block they just changed.
    rows.push({ kind: "pair", key: `block:${pair.block.index}`, pair });
    for (const node of after.get(pair.block.index) ?? []) rows.push(droppedRow(node));
  }
  return rows;
}

/** The `block.index` of the last pair at or before an anchor, or null when every pair is past it. */
function anchorPair(script: ScriptView, afterBlockIndex: number): number | null {
  let found: number | null = null;
  for (const pair of script.pairs) {
    if (pair.block.index <= afterBlockIndex) found = pair.block.index;
  }
  return found;
}

/**
 * Where j and k land. `Math.min` first would answer −1 on a Chapter with no
 * rows at all — a refining, pending, or excluded one — and a focus of −1
 * survives the arrival of the script on the next poll: no row carries
 * `aria-current`, nothing is in the tab order, and `p` has no Block to speak.
 */
export const clampFocus = (current: number, delta: number, length: number): number =>
  Math.max(0, Math.min(length - 1, current + delta));

const droppedRow = (node: DroppedNode): DroppedRow => ({
  kind: "dropped",
  key: `dropped:${node.id}`,
  node,
});

/** The pair a row shows, or null for a dropped node; the editor and the Sample both need it. */
export const pairOf = (row: ReviewRow | undefined): AlignmentPair | null =>
  row?.kind === "pair" ? row.pair : null;
